import { useState, useMemo } from "react";
import { useData } from "../context/DataContext";
import { calculateLehrpersonStats } from "../utils/calculations";
import {
  Plus,
  Trash2,
  Edit as EditIcon,
  ChevronDown,
  ChevronRight,
  Filter,
  X,
} from "lucide-react";
import { cn } from "../utils/cn";
import { InlineSelect } from "./InlineSelect";
import { Modal, Button, Input, EmptyState } from "./ui";
import { useExpandableRows, useDeleteConfirmation, useFormReset } from "../utils/hooks";
import { getStatusConfig } from "../utils/helpers";
import { ZUTEILUNG_TYPES } from "../utils/constants";

const Lehrpersonen = () => {
  const {
    data,
    addLehrperson,
    updateLehrperson,
    deleteLehrperson,
    addZuteilung,
    deleteZuteilung,
    updateZuteilung,
  } = useData();

  const [showModal, setShowModal] = useState(false);
  const [editingLpId, setEditingLpId] = useState<string | null>(null);
  const [selectedFach, setSelectedFach] = useState<string>("");
  const { toggleRow, isExpanded } = useExpandableRows();
  const { formData, setFormData, resetForm } = useFormReset({
    name: "",
    faecher: "",
    solllektionen: "24",
    geburtsdatum: "",
  });

  const confirmDelete = useDeleteConfirmation("Lehrperson", deleteLehrperson);

  // Get unique Fächer from all Lehrpersonen for filtering
  const availableFaecher = useMemo(() => {
    const faecherSet = new Set<string>();
    data.lehrpersonen.forEach((lp) => {
      lp.faecher.forEach((f) => faecherSet.add(f));
    });
    return Array.from(faecherSet).sort();
  }, [data.lehrpersonen]);

  // Filter Lehrpersonen by selected Fach
  const filteredLehrpersonen = useMemo(() => {
    if (!selectedFach) return data.lehrpersonen;
    return data.lehrpersonen.filter((lp) => lp.faecher.includes(selectedFach));
  }, [data.lehrpersonen, selectedFach]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = {
      name: formData.name,
      faecher: formData.faecher
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f),
      solllektionen: parseFloat(formData.solllektionen),
      geburtsdatum: formData.geburtsdatum || undefined,
    };
    if (editingLpId) {
      updateLehrperson(editingLpId, parsed);
    } else {
      addLehrperson(parsed);
    }
    resetForm();
    setEditingLpId(null);
    setShowModal(false);
  };

  const openEdit = (lp: (typeof data.lehrpersonen)[0]) => {
    setFormData({
      name: lp.name,
      faecher: lp.faecher.join(", "),
      solllektionen: String(lp.solllektionen),
      geburtsdatum: lp.geburtsdatum || "",
    });
    setEditingLpId(lp.id);
    setShowModal(true);
  };

  const handleAddZuteilung = (lpId: string) => {
    if (data.klassen.length === 0 || data.faecher.length === 0) {
      alert("Bitte erst Klassen und Fächer anlegen!");
      return;
    }
    addZuteilung({
      lpId,
      klasseId: data.klassen[0].id,
      fachId: data.faecher[0].id,
      typ: "Standard",
      lektionen: 2,
      hatHk: false,
      bemerkungen: "",
    });
  };

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
        >
          Neue Lehrperson
        </Button>

        {/* Fach Filter */}
        {availableFaecher.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={selectedFach}
              onChange={(e) => setSelectedFach(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="">Alle Fächer</option>
              {availableFaecher.map((fach) => (
                <option key={fach} value={fach}>
                  {fach}
                </option>
              ))}
            </select>
            {selectedFach && (
              <button
                onClick={() => setSelectedFach("")}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Filter zurücksetzen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Filter info */}
        {selectedFach && (
          <span className="text-sm text-gray-500">
            {filteredLehrpersonen.length} von {data.lehrpersonen.length} Lehrpersonen
          </span>
        )}
      </div>

      {/* Main Content */}
      {data.lehrpersonen.length === 0 ? (
        <EmptyState
          icon="👨‍🏫"
          title="Keine Lehrpersonen vorhanden."
          description='Klicken Sie auf "Neue Lehrperson" oder laden Sie Demo-Daten über die Sidebar'
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    {/* Expand icon */}
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fächer
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vertr. Lekt.
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eff. Soll
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ist Std
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entschäd.
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Differenz
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLehrpersonen.map((lp) => {
                  const stats = calculateLehrpersonStats(lp.id, data);
                  const statusConfig = getStatusConfig(stats.status);
                  const StatusIcon = statusConfig.icon;
                  const isRowExpanded = isExpanded(lp.id);
                  const lpZuteilungen = data.zuteilungen.filter(
                    (zt) => zt.lpId === lp.id
                  );

                  return (
                    <>
                      <tr
                        key={lp.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-2 whitespace-nowrap">
                          <button
                            onClick={() => toggleRow(lp.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            {isRowExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <div className="font-medium text-gray-900">
                            {lp.name}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1">
                            {lp.faecher.map((fach, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {fach}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {lp.solllektionen}
                          {stats.hatAltersentlastung && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Altersentlastung">
                              AE
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {stats.soll.toFixed(1)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                          {stats.ist.toFixed(1)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                          {stats.entschaedigung.toFixed(1)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                          <span
                            className={cn(
                              "font-semibold",
                              stats.differenz > 0
                                ? "text-red-600"
                                : stats.differenz < 0
                                ? "text-amber-600"
                                : "text-green-600"
                            )}
                          >
                            {stats.differenz > 0 ? "+" : ""}
                            {stats.differenz.toFixed(1)}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          <span
                            className={cn(
                              "inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium",
                              statusConfig.bg,
                              statusConfig.text
                            )}
                          >
                            <StatusIcon className="w-3.5 h-3.5" />
                            <span>{stats.status}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-center">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => openEdit(lp)}
                              className="inline-flex items-center p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Bearbeiten"
                            >
                              <EditIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => confirmDelete(lp.id, lp.name)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row - Assignments */}
                      {isRowExpanded && (
                        <tr key={`${lp.id}-expanded`}>
                          <td colSpan={10} className="px-4 py-3 bg-gray-50">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-900">
                                  Zuteilungen ({lpZuteilungen.length})
                                </h4>
                                <button
                                  onClick={() => handleAddZuteilung(lp.id)}
                                  className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  <span>Neue Zuteilung</span>
                                </button>
                              </div>

                              {lpZuteilungen.length === 0 ? (
                                <div className="text-center py-8 text-gray-400 text-sm">
                                  Keine Zuteilungen vorhanden
                                </div>
                              ) : (
                                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                  <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Klasse
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Fach
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Typ
                                        </th>
                                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                          Lektionen
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                          HK
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Bemerkungen
                                        </th>
                                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                          Aktion
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {lpZuteilungen.map((zt) => {
                                        return (
                                          <tr
                                            key={zt.id}
                                            className="hover:bg-gray-50"
                                          >
                                            <td className="px-4 py-2">
                                              <InlineSelect
                                                value={zt.klasseId}
                                                options={data.klassen.map(
                                                  (kl) => ({
                                                    id: kl.id,
                                                    label: `${kl.name} (${kl.bestand} SuS)`,
                                                  })
                                                )}
                                                onChange={(value) =>
                                                  value &&
                                                  updateZuteilung(zt.id, {
                                                    klasseId: value,
                                                  })
                                                }
                                                placeholder="?"
                                                displayClassName="text-sm text-gray-900"
                                              />
                                            </td>
                                            <td className="px-4 py-2">
                                              <InlineSelect
                                                value={zt.fachId}
                                                options={data.faecher.map(
                                                  (f) => ({
                                                    id: f.id,
                                                    label: `${f.kuerzel} - ${f.name}`,
                                                  })
                                                )}
                                                onChange={(value) =>
                                                  value &&
                                                  updateZuteilung(zt.id, {
                                                    fachId: value,
                                                  })
                                                }
                                                placeholder="?"
                                                displayClassName="text-sm text-gray-900"
                                              />
                                            </td>
                                            <td className="px-4 py-2">
                                              <InlineSelect
                                                value={zt.typ}
                                                options={ZUTEILUNG_TYPES}
                                                onChange={(value) =>
                                                  value &&
                                                  updateZuteilung(zt.id, {
                                                    typ: value as any,
                                                  })
                                                }
                                                displayClassName="text-xs text-gray-700"
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-right text-sm text-gray-900">
                                              <input
                                                type="number"
                                                min="0.5"
                                                step="0.5"
                                                value={zt.lektionen}
                                                onChange={(e) =>
                                                  updateZuteilung(zt.id, {
                                                    lektionen: parseFloat(
                                                      e.target.value
                                                    ),
                                                  })
                                                }
                                                className="w-16 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                              <input
                                                type="checkbox"
                                                checked={zt.hatHk}
                                                onChange={(e) =>
                                                  updateZuteilung(zt.id, {
                                                    hatHk: e.target.checked,
                                                  })
                                                }
                                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                              />
                                            </td>
                                            <td className="px-4 py-2">
                                              <input
                                                type="text"
                                                value={zt.bemerkungen}
                                                onChange={(e) =>
                                                  updateZuteilung(zt.id, {
                                                    bemerkungen: e.target.value,
                                                  })
                                                }
                                                placeholder="Bemerkungen..."
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                              />
                                            </td>
                                            <td className="px-4 py-2 text-center">
                                              <button
                                                onClick={() =>
                                                  deleteZuteilung(zt.id)
                                                }
                                                className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Löschen"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingLpId(null); resetForm(); }}
        title={editingLpId ? "Lehrperson bearbeiten" : "Neue Lehrperson"}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Name *"
            type="text"
            required
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="z.B. Max Mustermann"
          />
          <Input
            label="Fächer (komma-getrennt) *"
            type="text"
            required
            value={formData.faecher}
            onChange={(e) =>
              setFormData({ ...formData, faecher: e.target.value })
            }
            placeholder="z.B. D, BG, M"
          />
          <Input
            label="Vertragl. Lektionen"
            type="number"
            min="1"
            step="0.5"
            value={formData.solllektionen}
            onChange={(e) =>
              setFormData({ ...formData, solllektionen: e.target.value })
            }
            placeholder="z.B. 26"
          />
          <Input
            label="Geburtsdatum"
            type="date"
            value={formData.geburtsdatum}
            onChange={(e) =>
              setFormData({ ...formData, geburtsdatum: e.target.value })
            }
          />
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={() => { setShowModal(false); setEditingLpId(null); resetForm(); }}
              variant="secondary"
            >
              Abbrechen
            </Button>
            <Button type="submit" variant="primary">
              Speichern
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Lehrpersonen;
